import BaseTypeController from "vamos/cockpit/base/controller/BaseTypeController";
import Dialog from "sap/m/Dialog";
import JSONModel from "sap/ui/model/json/JSONModel";
import { Article, Product } from "vamos/cockpit/model/odata/VamosModel";
import ApiService from "vamos/cockpit/base/services/type/ApiService";
import Input, { Input$LiveChangeEvent } from "sap/m/Input";
import ErrorService from "vamos/cockpit/base/services/type/ErrorService";
import ProductVariantProperty from "vamos/cockpit/data/article/model/ProductVariantProperty";
import JsonUtil from "vamos/cockpit/base/util/JsonUtil";
import ClientSettingsService from "vamos/cockpit/base/services/ClientSettingsService";
import {
    ProductConfiguration,
    ProductTemplateConfiguration,
    ResponseBoolean,
    ResponseListArticleSupplier,
    ResponseProductTemplateConfiguration,
    ResponseString,
    SupplierConditionProperty,
} from "vamos/cockpit/model/api/vamos.private.api.types";
import V4Context from "sap/ui/model/odata/v4/Context";
import AppResponse from "vamos/cockpit/model/api/AppResponse";
import { Field$ChangeEvent } from "sap/ui/mdc/Field";
import { Button$PressEvent } from "sap/m/Button";
import ValidationUtil from "vamos/cockpit/base/util/ValidationUtil";
import ListBinding from "sap/ui/model/ListBinding";


export default class NewVariantDialogController extends BaseTypeController {
    private dialog: Dialog;
    private _oDialogModel: JSONModel;
    private article: Article;
    private productConfiguration: ProductConfiguration;
    private showSaveAndSupplierConditionButton: boolean;
    private defaultOffcut: number;
    private saveCallBack: (variant: Product, openSupplierConditionDialog: boolean) => void;

    onInit(): void {
        super.onInit();
        this.dialog = this.getView().byId("newVariantDialog") as Dialog;
    }

    public async openDialog(article: Article, product: Product, copyVariant: boolean, showSaveAndSupplierConditionButton: boolean, saveCallBack: (variant: Product, openSupplierConditionDialog: boolean) => void): Promise<void> {
        ValidationUtil.clearRequiredFields(this.dialog);
        this.saveCallBack = saveCallBack;
        this.article = article;
        this.setProductConfiguration(product);
        this.showSaveAndSupplierConditionButton = showSaveAndSupplierConditionButton;
        this._oDialogModel = new JSONModel({
            article: article,
            product: product,
            editMode: product == null,
            productConfiguration: this.productConfiguration,
            copyVariant: copyVariant,
            IsStockManaged: product?.IsStockManaged ?? true,
            IsCommissionArticle: product?.IsCommissionArticle ?? false,
            supplierTemplateGuid: null,
            newVariant: product == null,
            productName: product?.Name,
            templateSuppliers: [],
            showSaveAndSupplierConditionButton: showSaveAndSupplierConditionButton,
            showSaveButton: true,
            BaseUnitDecimalValue: product?.BaseUnitDecimalValue ?? false,
        });

        if (copyVariant) {
            this.productConfiguration.guid = null;
            this._oDialogModel.setProperty("/editMode", true);
            this._oDialogModel.setProperty("/newVariant", true);
            this._oDialogModel.setProperty("/IsStockManaged", true);
        }

        this.getView().setModel(this._oDialogModel, "variantDialog");
        await this.loadTemplates();
        await this.loadMatchingTemplates();
        await this.refreshEanFromServer();
        await this.refreshDefaultOffcut();
        if (product) {
            this.updateProperties(product);
        } else {
            this.updateOffcutDisplay(null);
        }
        this.dialog.open();
    }

    private updateProperties(product: Product) {
        this._oDialogModel.setProperty("/InnerLaminationGuid", product.InnerLaminationGuid);
        this._oDialogModel.setProperty("/OuterLaminationGuid", product.OuterLaminationGuid);
        this._oDialogModel.setProperty("/CarrierGuid", product.CarrierGuid);
        this._oDialogModel.setProperty("/GeometryGuid", product.GeometryGuid);
        this._oDialogModel.setProperty("/SealGuid", product.SealGuid);
        this._oDialogModel.setProperty("/SealColorGuid", product.SealColorGuid);
        this._oDialogModel.setProperty("/AdditionalEquipmentGuid", product.AdditionalEquipmentGuid);
        this._oDialogModel.setProperty("/BaseUnitGuid", product.BaseUnitGuid);
        this._oDialogModel.setProperty("/BaseAmount", product.BaseAmount);
        this._oDialogModel.setProperty("/Ean", product.Ean);
        this._oDialogModel.setProperty("/BaseUnitDecimalValue", product.BaseUnitDecimalValue);
        this.updateOffcutDisplay(product.Offcut);
        if(product.IsNameManuallySet){
            this._oDialogModel.setProperty("/Name", product.Name);


        }
    }

    private setProductConfiguration(product: Product) {
        if (product) {
            this.productConfiguration = {
                guid: product.Guid,
                name: product.Name,
                articleGuid: product.ArticleGuid,
                innerLaminationGuid: product.InnerLaminationGuid,
                outerLaminationGuid: product.OuterLaminationGuid,
                carrierGuid: product.CarrierGuid,
                geometryGuid: product.GeometryGuid,
                sealGuid: product.SealGuid,
                sealColorGuid: product.SealColorGuid,
                additionalEquipmentGuid: product.AdditionalEquipmentGuid,
                isNameManuallySet: product.IsNameManuallySet,
                /** @format double */
                baseAmount: product.BaseAmount,
                baseUnitGuid: product.BaseUnitGuid,
                ean: product.Ean,
            };
        } else {
            this.resetProductConfiguration();
        }
    }

    private resetProductConfiguration() {
        this.productConfiguration = {
            articleGuid: this.article.Guid,
            supplierTemplateGuid: this._oDialogModel?.getProperty("/supplierTemplateGuid") as string,
        };
    }

    private async loadTemplates() {
        const responseBoolean: ResponseBoolean = await ApiService.getInstance().getRestJsonData(`article/is-configurable/${this.article.Guid}`);
        const isConfigurable = responseBoolean.content;

        this._oDialogModel.setProperty("/showVariantConfiguration", isConfigurable);

        if (isConfigurable) {
            const reponseTemplateSuppliers: ResponseListArticleSupplier = await ApiService.getInstance().getRestJsonData(`article/find-supplier-with-templates/${this.article.Guid}`);
            const articleSuppliers = reponseTemplateSuppliers.content;
            if (articleSuppliers) {

                if (articleSuppliers.length > 0) {
                    this.productConfiguration.supplierTemplateGuid = articleSuppliers[0].supplierGuid;
                    this._oDialogModel?.setProperty("/supplierTemplateGuid", articleSuppliers[0].supplierGuid);
                    this.updateSaveButtons();
                    articleSuppliers.push({ supplierGuid: null, supplierName: this.getText("FreeConfiguration") });
                }
                this._oDialogModel.setProperty("/templateSuppliers", articleSuppliers);
            }
        }
    }

    private updateSaveButtons() {
        const selectedSupplierTemplateGuid = this._oDialogModel?.getProperty("/supplierTemplateGuid") as string;
        const editMode = this._oDialogModel?.getProperty("/editMode") as boolean;
        const selectedSupplierIsSet = selectedSupplierTemplateGuid === null || selectedSupplierTemplateGuid === "" || selectedSupplierTemplateGuid === undefined;
        this._oDialogModel.setProperty("/showSaveButton", !editMode || selectedSupplierIsSet);
        this._oDialogModel.setProperty("/showSaveAndSupplierConditionButton", editMode && (!selectedSupplierIsSet || this.showSaveAndSupplierConditionButton));
    }

    private addParsedNumber(elements: SupplierConditionProperty[]): ProductVariantProperty[] {
        return elements.map((oElement): ProductVariantProperty => {
            const property: ProductVariantProperty = oElement;

            if (property.number) {
                const iParsedNumber = parseInt(oElement.number);
                // to sort the valueValueHelps
                property.ParsedNumber = isNaN(iParsedNumber) ? property.number : iParsedNumber;
            }
            return property;
        });
    }

    private updateUIFields(productTemplateConfiguration: ProductTemplateConfiguration) {

        const oVhModel = this.getModel("ValueHelp") as JSONModel;
        oVhModel.setProperty("/MeasurementUnits", this.addParsedNumber(productTemplateConfiguration.baseUnits.map((content) => JsonUtil.capitalizeFirstLetters(content))));
        oVhModel.setProperty("/Geometries", this.addParsedNumber(productTemplateConfiguration.geometries.map((content) => JsonUtil.capitalizeFirstLetters(content))));
        this._oDialogModel.setProperty("/BaseAmount", productTemplateConfiguration.baseAmount);
        if (productTemplateConfiguration.defaultBaseUnit) {
            this._oDialogModel.setProperty(`/BaseUnitGuid`, productTemplateConfiguration.defaultBaseUnit.guid);
            this._oDialogModel.setProperty(`/BaseUnitName`, productTemplateConfiguration.defaultBaseUnit.name);
        }

        const arFields = [{
            properties: productTemplateConfiguration.outerLaminations,
            fieldName: "OuterLamination",
            defaultValue: productTemplateConfiguration.defaultOuterLamination,
        },
            {
                properties: productTemplateConfiguration.innerLaminations,
                fieldName: "InnerLamination",
                defaultValue: productTemplateConfiguration.defaultInnerLamination,
            },
            {
                properties: productTemplateConfiguration.carriers,
                fieldName: "Carrier",
                defaultValue: productTemplateConfiguration.defaultCarrier,
            },
            {
                properties: productTemplateConfiguration.geometries,
                fieldName: "Geometry",
                defaultValue: productTemplateConfiguration.defaultGeometry,
            }, {
                properties: productTemplateConfiguration.seals,
                fieldName: "Seal",
                defaultValue: productTemplateConfiguration.defaultSeal,
            },
            {
                properties: productTemplateConfiguration.sealColors,
                fieldName: "SealColor",
                defaultValue: productTemplateConfiguration.defaultSealColor,
            },
            {
                properties: productTemplateConfiguration.additionalEquipments,
                fieldName: "AdditionalEquipment",
                defaultValue: productTemplateConfiguration.defaultAdditionalEquipment,
            },
            {
                properties: productTemplateConfiguration.baseUnits,
                fieldName: "BaseUnit",
                defaultValue: productTemplateConfiguration.defaultBaseUnit,
            }];
        for (const fieldObject of arFields) {
            const fieldContent = fieldObject.properties;
            if (fieldContent) {
                oVhModel.setProperty(`/${fieldObject.fieldName}s`, this.addParsedNumber(fieldContent.map((content) => JsonUtil.capitalizeFirstLetters(content))));
            }
            let modelIsSet = false;
            if (fieldObject.defaultValue) {
                const selectedProperty = fieldObject.defaultValue;
                if (selectedProperty) {
                    modelIsSet = true;
                    this._oDialogModel.setProperty(`/${fieldObject.fieldName}Guid`, selectedProperty.guid);
                    this._oDialogModel.setProperty(
                        `/${fieldObject.fieldName}Name`,
                        (selectedProperty.displayName = selectedProperty.displayName ? selectedProperty.number + " - " + selectedProperty.displayName : selectedProperty.name),
                    );
                }
            }

            if (!modelIsSet) {
                this._oDialogModel.setProperty(`/${fieldObject.fieldName}Guid`, "");
                this._oDialogModel.setProperty(`/${fieldObject.fieldName}Name`, "");
            }

            if (fieldObject.fieldName === "BaseUnit") {
                this._oDialogModel.setProperty("/BaseUnitDecimalValue", !!fieldObject.defaultValue?.decimalValue);
            }
        }

    }


    private async loadMatchingTemplates() {

        const apiResponse = await ApiService.getInstance().postRestJsonData("/product/matching-templates", this.productConfiguration);
        const configuration = apiResponse.data as ResponseProductTemplateConfiguration;
        this.updateUIFields(configuration.content);
    }

    public onCloseDialog() {
        this.dialog.close();
    }

    public checkInputFilled(event: Input$LiveChangeEvent) {
        const value = event.getParameter("value");
        if (value !== "") {
            event.getSource().setValueState("None");
        } else {
            event.getSource().setValueState("Error");
        }
    }

    public async onResetConfiguration(oEvent) {
        this.resetProductConfiguration();
        this.updateSaveButtons();
        await this.loadMatchingTemplates();
    }


    public async onChangeConfig() {
        this.updateProductConfiguration();
        await this.loadMatchingTemplates();
    };

    private setBaseAmountValue(isSelectedUnitDecimal: boolean) {
        const oInput = this.getView().byId("BaseUnitAmountInput") as Input;
        if (!oInput) {
            return;
        }
        oInput.setEnabled(isSelectedUnitDecimal);
        if (!isSelectedUnitDecimal) {
            oInput.setValue("1");
        }
    }

    public onBaseUnitChange(oEvent: Field$ChangeEvent) {
        if (!oEvent.getParameter("valid")) {
            return;
        }
        const sValueHelpId = oEvent.getSource().getValueHelp();
        const oTableBinding = this.getView().byId(`${sValueHelpId}-table`).getBinding("items") as ListBinding;
        const arContexts = oTableBinding.getAllCurrentContexts();
        const oSelectedContext = arContexts.find((oContext) => {
            return oContext.getProperty("Guid") === oEvent.getParameter("value");
        });

        const isDecimalValue = oSelectedContext.getProperty("DecimalValue") as boolean;
        this.setBaseAmountValue(isDecimalValue);
        this._oDialogModel.setProperty("/BaseUnitDecimalValue", isDecimalValue);
        if (!isDecimalValue) {
            this.updateOffcutDisplay(null);
        }
    }

    public onResetOffcut(): void {
        this.updateOffcutDisplay(null);
    }

    private updateOffcutDisplay(offcut: number): void {
        const oInput = this.getView().byId("VariantOffcutInput") as Input;
        const dOffcut = offcut != null ? offcut : this.defaultOffcut;
        oInput?.setValue((dOffcut * 100).toFixed(2));
    }


    public async onResetField(oEvent: Button$PressEvent) {
        const oSource = oEvent.getSource().getParent().getAggregation("items")[0];
        oSource.setValue(undefined);
        oSource.setAdditionalValue(undefined);
        oSource.getBinding("value").setValue(undefined);
        await this.onChangeConfig();
    };

    public async onUseEan(oEvent) {
        await this.refreshEanFromServer();
        this.updateProductConfiguration();
    }

    public async onSaveNewVariant() {
        await this.onSave(false);
    }

    public async onSaveAndSupplierCondition() {
        await this.onSave(true);
    }

    public async onSave(openSupplierConditionDialog: boolean): Promise<void> {
        this.dialog.setBusy(true);
        this.updateProductConfiguration();
        const productName = this._oDialogModel.getProperty("/Name") as string;
        this.productConfiguration.isNameManuallySet = ![null, undefined, ""].includes(productName);
        this.productConfiguration.name = productName;
        this.productConfiguration.isStockManaged = this._oDialogModel.getProperty("/IsStockManaged") as boolean;
        this.productConfiguration.isCommissionArticle = this._oDialogModel.getProperty("/IsCommissionArticle") as boolean;
        try {
            let response: AppResponse;
            let productGuid: string;
            if (this.productConfiguration.guid) {
                response = await ApiService.getInstance().putRestJsonDataWithoutMessage("/product", this.productConfiguration);
                productGuid = this.productConfiguration.guid;
            } else {
                response = await ApiService.getInstance().postRestJsonDataWithoutMessage("/product", this.productConfiguration);
                const productResponse: ResponseString = response.data as ResponseString;
                productGuid = productResponse?.content;
            }

            if (response.success) {

                const product = await this.loadProduct(productGuid);

                if (this.productConfiguration.supplierTemplateGuid) {
                    openSupplierConditionDialog = false;
                }

                this.saveCallBack(product, openSupplierConditionDialog);
                this.onCloseDialog();
            } else {
                const reponseData = response.data as ResponseString;

                const errors = reponseData.additionalInfo.filter((info: any) => info.Field == "Ean" || info.Field == "IsStockManaged");
                if (errors.length > 0) {
                    ErrorService.getInstance().showErrorMessageBox(errors.map((error: any) => error.Message).join("\n"), "Error");
                } else {
                    ErrorService.getInstance().showErrorMessageBox(this.getText("ProductVariantSavingError"), "Error");
                }
                this.onSaveFail(response.data as ResponseString);
            }
        } catch (e) {
            console.error(e);
        } finally {
            this.dialog.setBusy(false);
        }

    }

    private async loadProduct(productGuid: string): Promise<Product> {
        const sPath = this.getKeyPredicateFromEntitySet("Product", productGuid);
        const contextBinding: V4Context = this.getModel().bindContext(sPath);
        return await contextBinding.requestObject() as Product;
    }

    private onSaveFail(responseContent: ResponseString) {
        responseContent.additionalInfo.forEach((val) => {
            const oInput = this.byId(val.Field as string) as Input;
            if (oInput) {
                oInput.setValueState("Error");
            }
        });
    };

    private async refreshEanFromServer() {
        const apiResponse: ResponseString = await ApiService.getInstance().getRestJsonData("/product/ean");
        this._oDialogModel.setProperty("/Ean", apiResponse.content);
    }

    private async refreshDefaultOffcut() {
        const defaultOffcut = await ClientSettingsService.getInstance().getByParamKey("default-meter-article-offcut") as string;
        this.defaultOffcut = parseFloat(defaultOffcut);
    }

    private getOffcutValue(): number {
        if (!this._oDialogModel.getProperty("/BaseUnitDecimalValue")) {
            return null;
        }
        const oInput = this.getView().byId("VariantOffcutInput") as Input;
        const sValue = oInput?.getValue();
        return sValue !== "" && sValue != null ? parseFloat(sValue) : null;
    }

    private updateProductConfiguration() {
        this.productConfiguration.innerLaminationGuid = this._oDialogModel.getProperty("/InnerLaminationGuid") as string;
        this.productConfiguration.outerLaminationGuid = this._oDialogModel.getProperty("/OuterLaminationGuid") as string;
        this.productConfiguration.carrierGuid = this._oDialogModel.getProperty("/CarrierGuid") as string;
        this.productConfiguration.geometryGuid = this._oDialogModel.getProperty("/GeometryGuid") as string;
        this.productConfiguration.sealGuid = this._oDialogModel.getProperty("/SealGuid") as string;
        this.productConfiguration.sealColorGuid = this._oDialogModel.getProperty("/SealColorGuid") as string;
        this.productConfiguration.additionalEquipmentGuid = this._oDialogModel.getProperty("/AdditionalEquipmentGuid") as string;
        this.productConfiguration.baseUnitGuid = this._oDialogModel.getProperty("/BaseUnitGuid") as string;
        this.productConfiguration.baseAmount = this._oDialogModel.getProperty("/BaseAmount") as number;
        this.productConfiguration.ean = this._oDialogModel.getProperty("/Ean") as string;
        this.productConfiguration.offcut = this.getOffcutValue();
    }

}